import { Typography } from 'antd';
import { useLayoutEffect } from 'react';

import {
  CODE_DESTINATION,
  CODE_LENGTH_BY_CHANNEL,
  DEFAULT_CODE_LENGTH,
  QR_INSTRUCTIONS,
  LONG_WAIT_HINTS_QR,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/constants';
import { useQrSessionAuth } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/hooks';
import {
  AuthStepState,
  type AuthStepInfo,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/types';
import { AuthStepView } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/ui/AuthStepView';
import { PasswordForm } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/ui/PasswordForm';
import { PhoneCodeForm } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/ui/PhoneCodeForm';

import type { FC } from 'react';

const { Text, Link } = Typography;

export interface QrSessionAuthContentProps {
  channelType: string;
  integrationId: number | null;
  activeBranchId: number | null;
  stepInfo: AuthStepInfo;
  isActive: boolean;
  onAuthorizationActivityChange?(isAuthorizationActive: boolean): void;
}

/**
 * Архетип «QR-сессия» (Telegram — личный, MAX): QR сразу, запасной путь —
 * номер и код, шаг пароля 2FA при необходимости (task/10730).
 */
export const QrSessionAuthContent: FC<QrSessionAuthContentProps> = ({
  channelType,
  integrationId,
  activeBranchId,
  stepInfo,
  isActive,
  onAuthorizationActivityChange,
}) => {
  const {
    mode,
    qr,
    isCodeStage,
    resendSecondsLeft,
    isPhoneSending,
    isCodeSending,
    isPasswordSending,
    isPhoneAuthError,
    submitPhone,
    submitCode,
    submitPassword,
    switchToPhone,
    switchToQr,
    retryPhoneAuth,
    isRequestQrExhausted,
    requestQrError,
    getQrError,
    isQrLoading,
    retryRequestQr,
    retryScanned,
    isScannedWaitOver,
  } = useQrSessionAuth({ integrationId, activeBranchId, stepInfo, isActive });

  const hasPhoneAuthError =
    mode === 'phone' &&
    isPhoneAuthError &&
    stepInfo.state === AuthStepState.Error;
  const hasVisibleError =
    hasPhoneAuthError ||
    (mode === 'qr' &&
      !isQrLoading &&
      !qr &&
      (isRequestQrExhausted ||
        Boolean(getQrError) ||
        stepInfo.state === AuthStepState.Error));
  const hasTimedOutScan =
    mode === 'qr' &&
    !isQrLoading &&
    stepInfo.state === AuthStepState.QrScanned &&
    isScannedWaitOver;
  const isAuthorizationActive =
    isActive &&
    stepInfo.state !== AuthStepState.Success &&
    !hasVisibleError &&
    !hasTimedOutScan;

  useLayoutEffect(() => {
    onAuthorizationActivityChange?.(isAuthorizationActive);
  }, [isAuthorizationActive, onAuthorizationActivityChange]);

  const instructionSteps = QR_INSTRUCTIONS[channelType] ?? [];
  // Инструкция нужна и во время загрузки: макет шага один, меняется только
  // содержимое места под QR-код (issue/11235).
  const instruction = (
    <>
      {instructionSteps.map((stepText, index) => (
        <div
          key={stepText}
          className="connect-integration-modal__instruction-step"
        >
          <span className="connect-integration-modal__instruction-number">
            {index + 1}
          </span>
          <Text>{stepText}</Text>
        </div>
      ))}
      <Text type="secondary">
        Не получается отсканировать?{' '}
        <Link onClick={switchToPhone} data-testid="connect-auth-to-phone">
          Войти по номеру телефона
        </Link>
      </Text>
    </>
  );

  if (stepInfo.state === AuthStepState.Password2fa) {
    return (
      <PasswordForm isSending={isPasswordSending} onSubmit={submitPassword} />
    );
  }

  if (stepInfo.state === AuthStepState.Success) {
    return <AuthStepView stepInfo={stepInfo} />;
  }

  if (hasPhoneAuthError) {
    return <AuthStepView stepInfo={stepInfo} onRetry={retryPhoneAuth} />;
  }

  // Режим пользователя важнее сохранённого qr_scanned: раньше клик менял
  // mode, но ранний return ниже снова рисовал тот же экран и кнопка была
  // фактически мёртвой (issue/11036).
  if (mode === 'phone') {
    return (
      <PhoneCodeForm
        codeDestinationText={CODE_DESTINATION[channelType] ?? 'Код отправлен'}
        codeLength={CODE_LENGTH_BY_CHANNEL[channelType] ?? DEFAULT_CODE_LENGTH}
        isCodeStage={isCodeStage}
        resendSecondsLeft={resendSecondsLeft}
        isPhoneSending={isPhoneSending}
        isCodeSending={isCodeSending}
        onSubmitPhone={submitPhone}
        onSubmitCode={submitCode}
        onBackToQr={switchToQr}
      />
    );
  }

  // Ошибка свежей локальной попытки важнее сохранённого шага CRM: иначе
  // auth_failed/qr_scanned заслоняет настоящую причину и кнопку повтора.
  if ((isRequestQrExhausted || getQrError) && !qr) {
    return (
      <AuthStepView
        stepInfo={{
          state: AuthStepState.Error,
          errorMessage:
            requestQrError ??
            getQrError ??
            'Не получилось запросить код у шлюза. Попробуйте ещё раз.',
        }}
        onRetry={retryRequestQr}
      />
    );
  }

  if (isQrLoading) {
    return (
      <AuthStepView
        stepInfo={{ state: AuthStepState.Loading }}
        longWaitHints={LONG_WAIT_HINTS_QR}
        showQrProgress
        instruction={instruction}
      />
    );
  }

  // Шаг после скана вынесен отдельно: у него есть предел ожидания, и по его
  // истечении нужны действия, а обработчики живут здесь (issue/11036).
  if (stepInfo.state === AuthStepState.QrScanned && !qr) {
    return (
      <AuthStepView
        stepInfo={stepInfo}
        isScannedWaitOver={isScannedWaitOver}
        onRetry={retryScanned}
        onSwitchToPhone={switchToPhone}
      />
    );
  }

  if (stepInfo.state === AuthStepState.Error && !qr) {
    return <AuthStepView stepInfo={stepInfo} onRetry={retryRequestQr} />;
  }

  // Полученная картинка QR важнее подстатуса: MAX-шлюз qr_ready не шлёт
  // вообще (только pending_password/auth_failed), и без этого окно висело
  // бы на скелетоне с уже готовым кодом (task/10886).
  const effectiveStepInfo =
    [
      AuthStepState.Loading,
      AuthStepState.QrExpired,
      AuthStepState.QrScanned,
      AuthStepState.Error,
    ].includes(stepInfo.state) && qr
      ? { state: AuthStepState.QrReady }
      : stepInfo;

  return (
    <AuthStepView
      stepInfo={effectiveStepInfo}
      qr={qr}
      onRefreshQr={retryRequestQr}
      showQrProgress={effectiveStepInfo.state === AuthStepState.Loading}
      longWaitHints={LONG_WAIT_HINTS_QR}
      instruction={instruction}
    />
  );
};
